import { BehaviorSubject, from, Observable, Subject, Subscription } from "rxjs";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { DiscoveryService } from "../discovery.service";
import { IWebResource } from "heracles/DataModel/IWebResource";
import { IHydraClient } from "heracles/IHydraClient";
import { IApiDocumentation } from "heracles/DataModel/IApiDocumentation";
import { IResource } from "heracles/DataModel/IResource";
import { ISupportedProperty } from "heracles/DataModel/ISupportedProperty";
import ResourceFilterableCollection from "heracles/DataModel/Collections/ResourceFilterableCollection";
import TypesCollection from "heracles/DataModel/Collections/TypesCollection";
import { hydra } from "heracles/namespaces";
import { IHypermediaContainer } from "hydraclient.js/types/src/DataModel/IHypermediaContainer";
import {ICollection} from "hydraclient.js/types/src/DataModel/ICollection";
import {IPartialCollectionView} from "hydraclient.js/types/src/DataModel/IPartialCollectionView";
const jsonld = require("jsonld");

const defaultProperty: ISupportedProperty = {
  iri: "",
  property: {
    iri: "@id",
    displayName: "IRI",
    description: "",
    type: new TypesCollection([]),
    valuesOfType: new ResourceFilterableCollection<IResource>([])
  },
  type: new TypesCollection([]),
  required: true,
  readable: true,
  writable: false
};

@Component({
  selector: "hydra-browser",
  templateUrl: "./hydra-browser.component.html",
  styleUrls: ["./hydra-browser.component.scss"]
})
export class HydraBrowserComponent implements OnInit, OnDestroy {
  public readonly documentation: Observable<IApiDocumentation>;
  public readonly selectedResource: Observable<IWebResource>;
  public requestedResource: any;
  public supportedProperties: Array<ISupportedProperty>;
  public members: Array<IResource>;
  public more: IResource;

  private readonly selectedResourceSubscription: Subscription;
  private readonly _selectedResource: Subject<any>;
  private _iri: string;

  public constructor(
    @Inject(Router) private router: Router,
    @Inject(DiscoveryService) private discoveryService: DiscoveryService,
    @Inject("HydraClient") private hydraClient: IHydraClient) {
    this._selectedResource = new BehaviorSubject(null);
    this.selectedResource = this._selectedResource.asObservable();
    this.documentation = this.discoveryService.documentation;
    this.selectedResourceSubscription = this.discoveryService.selectedResource.subscribe(_ => this.reset(_));
    this.members = [];
    this.more = null;
  }

  public get hasMembers(): boolean {
    return this.members.length > 0;
  }

  public makeLabelFor(resource: any): string {
    return this.discoveryService.makeLabelFor(resource) || this.discoveryService.makeLabelFor(resource.property);
  }

  public getValueOf(resource: any, predicateIri: string): any {
    let result = "";
    if (!!resource) {
      const value = resource[predicateIri];
      if (!!value && value.length > 0) {
        if (typeof(value) === "string") {
          result = value;
        } else {
          result = value[0]["@id"] || (value[0]["@value"] || "");
        }
      }
    }

    return result;
  }

  public getSupportedProperties(documentation: IApiDocumentation): Iterable<ISupportedProperty> {
    this.supportedProperties = [];

    const commonTypes = this.gatherCommonTypes(this.members);
    if (commonTypes != null) {
      for (const type of commonTypes) {
        for (const supportedClass of documentation.supportedClasses.ofIri(type)) {
          for (const supportedProperty of supportedClass.supportedProperties) {
            if (!this.supportedProperties[supportedProperty.property.iri]) {
              this.supportedProperties[supportedProperty.property.iri] = supportedProperty;
              this.supportedProperties.push(supportedProperty);
            }
          }
        }
      }
    }

    if (this.supportedProperties.length === 0) {
      this.supportedProperties.push(defaultProperty);
    }

    return this.supportedProperties;
  }

  public load(resource: IResource): void {
    from(this.hydraClient.getResource(resource)).subscribe(_ => this.onSelectedResourceAvailable(_));
  }

  public ngOnInit(): void {
    if (!this.discoveryService.isResourceAvailable) {
      this.router.navigate(["/"]);
    }
  }

  public ngOnDestroy(): void {
    this.selectedResourceSubscription.unsubscribe();
  }

  private reset(resource: IWebResource): void {
    this.members = [];
    this._iri = null;
    this.onSelectedResourceAvailable(resource);
  }

  private onSelectedResourceAvailable(resource: IWebResource): void {
    if (!!resource) {
      from(jsonld.flatten(resource)).subscribe(_ => this.onJsonLdExpanded(resource, _ as Array<any>));
    } else if (!this._iri) {
      this._selectedResource.next(null);
    }
  }

  private onJsonLdExpanded(resource: IWebResource, flattened: Array<any>): void {
    let view: IPartialCollectionView = null;
    const members = [];
    let sourceMembers = [];
    if (!this._iri) {
      this._iri = resource.hypermedia.iri;
      view = resource.hypermedia.view as IPartialCollectionView;
      if (!!resource.hypermedia.members) {
        sourceMembers = resource.hypermedia.members;
      }
    } else {
      const collection = resource.hypermedia.collections.ofIri(this._iri).first();
      if (!!collection) {
        view = collection.view as IPartialCollectionView;
        if (!!collection.members) {
          sourceMembers = collection.members;
        }
      }
    }

    for (const member of this.members) {
      members.push(member);
    }

    for (const member of sourceMembers) {
      (member as any).representation = flattened.find(_ => _["@id"] === member.iri);
      members.push(member);
    }

    this.members = members;
    if (!!view) {
      this.more = view.links.withRelationOf(hydra.next).first();
    } else {
      this.more = null;
    }

    this._selectedResource.next(this.requestedResource = flattened.find(_ => _["@id"] === this._iri));
  }

  private gatherCommonTypes(members: Iterable<IResource>): Array<string> {
    let commonTypes = null;
    for (const member of members) {
      if (commonTypes === null) {
        commonTypes = [...member.type];
      } else {
        for (let index = commonTypes.length - 1; index >= 0; index--) {
          if (!member.type.contains(commonTypes[index])) {
            commonTypes.splice(index, 1);
          }
        }
      }
    }

    return commonTypes || [];
  }
}
